import React from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { dataFormStyles } from '~/modules/covenants/CovenantsDataForm/styles.ts';
import { guaranteesClientText } from '~/modules/guarantees/form/client/constants.ts';
import { guaranteesClientPageStyles } from '~/modules/guarantees/form/client/styles.ts';
import { GuaranteeOperationType } from '~/store/guarantees/types.ts';
import {
  BENEFICIARY_REQUIRED_FIELDS,
  GUARANTEE_FORM_REQUIRED_FIELDS,
  MAIN_DEAL_FIELD_NAMES,
  PRINCIPAL_REQUIRED_FIELDS,
  ROOT_SECTION_FIELD_NAMES,
} from '../../rootSection/constants.ts';
import type { TGuaranteesContractOption, TGuaranteesRootSectionFormValues } from '../../rootSection/types.ts';
import {
  areGuaranteesRequiredSelectionsComplete,
  getGuaranteesContractOptions,
  getGuaranteesApplicationMainDealId,
  getGuaranteesRootTenderPublicId,
  getSelectedGuaranteeOperationType,
  isGuaranteeOperationTypeProzorro,
  isGuaranteesOperationTypeChanged,
  saveGuaranteesStoredContractSelection,
  areRequiredValuesFilled,
  isGuaranteesCheckApplicationDisabled,
  validateGuaranteesRootSectionForm,
  validateGuaranteesSecretSectionForm,
  shouldLoadGuaranteesDependentSections,
} from '../../rootSection/utils.ts';
import {
  getGuaranteesBeneficiaryInitialValues,
  getGuaranteesFormInitialValues,
  getGuaranteesPrincipalInitialValues,
} from '../../utils/sections/utils.ts';
import AccountSection from './AccountSection/AccountSection.tsx';
import BaseRelationsSection from './BaseRelationsSection/BaseRelationsSection.tsx';
import BeneficiarySection from './BeneficiarySection/BeneficiarySection.tsx';
import GuaranteeFormSection from './GuaranteeFormSection/GuaranteeFormSection.tsx';
import GuaranteeTypeSection from './GuaranteeTypeSection/GuaranteeTypeSection.tsx';
import MainDealSection from './MainDealSection/MainDealSection.tsx';
import PrincipalSection from './PrincipalSection/PrincipalSection.tsx';
import ProzorroTenderSection from './ProzorroTenderSection/ProzorroTenderSection.tsx';
import {
  GuaranteesRootApplicationSectionProvider,
  type TGuaranteesRootDependentSectionKey,
} from './RootApplicationSectionContext.tsx';
import {
  applyGuaranteesContractOptionToForm,
  areSameContractOptions,
  clearGuaranteesContractSelection,
  findMatchingContractOption,
  isGuaranteesContractOption,
} from './selectionUtils.ts';
import type { IGuaranteesRootApplicationSectionProps } from './types.ts';
import { useRootAccountSection } from './hooks/useRootAccountSection.ts';

const dependentSectionDirtyInitialState = {
  principal: false,
  beneficiary: false,
  baseRelations: false,
  guaranteeForm: false,
};

const GuaranteesRootApplicationSection: React.FC<IGuaranteesRootApplicationSectionProps> = ({
  application,
  applicationTenderInfo,
  applicationId,
  storedContractSelection,
  accounts,
  principalDebitAccounts,
  mainDeals,
  principal,
  beneficiary,
  baseRelations,
  guaranteeForm,
  guaranteeCurrencyOptions,
  prozorroTender,
  isLoadingCurrencies,
  isLoadingSecurityCurrencies,
  isLoadingMainDeals,
  isLoadingAccounts,
  isLoadingPrincipalDebitAccounts,
  isLoadingPrincipal,
  isLoadingBeneficiary,
  isLoadingBaseRelations,
  isLoadingGuaranteeForm,
  isSearchingTender,
  isSelectingTender,
  isSelectingMainDeal,
  isCheckingApplication,
  isSaving,
  isSavingAccount,
  isSavingPrincipal,
  isSavingBeneficiary,
  isSavingBaseRelations,
  isSavingGuaranteeForm,
  isReadOnly,
  requireChangesToEnableActions = false,
  checkApplicationButtonLabel = guaranteesClientText.checkApplicationButton,
  shouldShowDependentSections,
  savedGuaranteeOperationType,
  onCheckApplication,
  onSubmit,
  onAccountsLoad,
  onPrincipalDebitAccountsLoad,
  onSecurityCurrenciesLoad,
  onSecretSubmit,
  onPrincipalLoad,
  onPrincipalSubmit,
  onBeneficiaryLoad,
  onBeneficiarySubmit,
  onBaseRelationsLoad,
  onBaseRelationsSubmit,
  onGuaranteeFormLoad,
  onGuaranteeFormSubmit,
  onTenderSearch,
  onTenderSelect,
  onMainDealSelect,
}) => {
  const {
    control,
    formState: { dirtyFields },
    getValues,
    resetField,
    setValue,
  } = useFormContext<TGuaranteesRootSectionFormValues>();
  const baseContractOptions = React.useMemo(
    () => getGuaranteesContractOptions(mainDeals),
    [mainDeals]
  );
  const guaranteeOperationType = useWatch({
    control,
    name: 'guaranteeOperationType',
  });
  const watchedRootValues = useWatch({
    control,
  }) as Partial<TGuaranteesRootSectionFormValues>;
  const watchedContract = useWatch({
    control,
    name: 'contract',
  });
  const selectedMainDealId = useWatch({
    control,
    name: 'mainDealId',
  });
  const selectedContract = React.useMemo(
    () => {
      const watchedContractOption = isGuaranteesContractOption(watchedContract)
        ? watchedContract
        : null;

      if (watchedContractOption) {
        return (
          findMatchingContractOption(baseContractOptions, watchedContractOption) ??
          watchedContractOption
        );
      }

      return (
        baseContractOptions.find(
          (option) => option.mainDealId && option.mainDealId === selectedMainDealId
        ) ?? null
      );
    },
    [baseContractOptions, selectedMainDealId, watchedContract]
  );
  const selectedGuaranteeOperationType = getSelectedGuaranteeOperationType(guaranteeOperationType);
  const isGuaranteeTypeSelected = !!selectedGuaranteeOperationType;
  const isProzorroTender = isGuaranteeOperationTypeProzorro(selectedGuaranteeOperationType);
  const isGuaranteeTypeChanged =
    !!dirtyFields.guaranteeOperationType &&
    isGuaranteesOperationTypeChanged({
      selectedOperationType: selectedGuaranteeOperationType,
      savedOperationType: savedGuaranteeOperationType,
    });
  const savedSectionOperationType = isGuaranteeTypeChanged ? null : savedGuaranteeOperationType;
  const isSavedGuaranteeTypeSelected = !!savedSectionOperationType;
  const isSavedProzorroTender = isGuaranteeOperationTypeProzorro(savedSectionOperationType);
  const shouldSaveGuaranteeTypeBeforeContinue =
    isGuaranteeTypeChanged || (isGuaranteeTypeSelected && !savedGuaranteeOperationType);
  const isOtherOperationType = selectedGuaranteeOperationType === GuaranteeOperationType.OTHER;
  const lockedUntilSavedHint = shouldSaveGuaranteeTypeBeforeContinue
    ? guaranteesClientText.saveGuaranteeTypeBeforeContinue
    : guaranteesClientText.lockedUntilGuaranteeTypeSelected;
  const prozorroDisabledHint = shouldSaveGuaranteeTypeBeforeContinue
    ? guaranteesClientText.saveGuaranteeTypeBeforeContinue
    : isSavedGuaranteeTypeSelected
      ? guaranteesClientText.prozorroOnlyDisabledHint
      : guaranteesClientText.lockedUntilGuaranteeTypeSelected;
  const displayedTenderPublicId =
    prozorroTender?.tenderPublicId || getGuaranteesRootTenderPublicId(application);
  const savedTenderPublicId = getGuaranteesRootTenderPublicId(application);
  const isNextStepAvailable = !!savedGuaranteeOperationType && !isGuaranteeTypeChanged;
  const loadedDependentSectionsRef = React.useRef('');
  const applicationLoadKey = application?.applicationId || application?.id || applicationId;
  const applicationSavedMainDealId = getGuaranteesApplicationMainDealId(
    application,
    storedContractSelection
  );
  const [savedMainDealId, setSavedMainDealId] = React.useState(applicationSavedMainDealId);
  const hasSavedMainDealInCurrentSessionRef = React.useRef(false);
  const [hasTenderPatchSuccess, setHasTenderPatchSuccess] = React.useState(false);
  const [hasMainDealPatchSuccess, setHasMainDealPatchSuccess] = React.useState(false);
  const [hasTenderSelectionChanges, setHasTenderSelectionChanges] = React.useState(false);
  const [hasMainDealSelectionChanges, setHasMainDealSelectionChanges] = React.useState(false);
  const [requiresNewTenderSelection, setRequiresNewTenderSelection] = React.useState(false);
  const [requiresNewMainDealSelection, setRequiresNewMainDealSelection] = React.useState(false);
  const previousSelectionApplicationKeyRef = React.useRef(applicationLoadKey);
  const canCheckApplication =
    !!applicationLoadKey && isNextStepAvailable && shouldShowDependentSections;
  const hasGuaranteeTypeChanges = Boolean(
    dirtyFields.guaranteeOperationType ||
      dirtyFields.guaranteeOperationTypeOther ||
      dirtyFields.guaranteeAmount ||
      dirtyFields.guaranteeCurrency ||
      dirtyFields.guaranteeExpiryDate ||
      dirtyFields.isUrgent
  );
  const hasTenderSelection =
    !hasTenderSelectionChanges &&
    (!requiresNewTenderSelection || hasTenderPatchSuccess) &&
    (!!savedTenderPublicId || hasTenderPatchSuccess);
  const hasMainDealSelection =
    !hasMainDealSelectionChanges &&
    (!requiresNewMainDealSelection || hasMainDealPatchSuccess) &&
    (!!savedMainDealId || hasMainDealPatchSuccess);
  const principalValues = getGuaranteesPrincipalInitialValues(principal);
  const beneficiaryValues = getGuaranteesBeneficiaryInitialValues(beneficiary);
  const guaranteeFormValues = getGuaranteesFormInitialValues(guaranteeForm);
  const isPrincipalSectionDataComplete = areRequiredValuesFilled(
    principalValues,
    PRINCIPAL_REQUIRED_FIELDS
  );
  const isBeneficiarySectionDataComplete = areRequiredValuesFilled(
    beneficiaryValues,
    BENEFICIARY_REQUIRED_FIELDS
  );
  const isGuaranteeFormSectionDataComplete = areRequiredValuesFilled(
    guaranteeFormValues,
    GUARANTEE_FORM_REQUIRED_FIELDS
  );
  const [dependentSectionDirtyMap, setDependentSectionDirtyMap] = React.useState(
    dependentSectionDirtyInitialState
  );
  const [dependentSectionSavedMap, setDependentSectionSavedMap] = React.useState({
    principal: false,
    beneficiary: false,
    guaranteeForm: false,
  });
  const previousDependentSectionsApplicationKeyRef = React.useRef(applicationLoadKey);

  React.useEffect(() => {
    const isSameApplication =
      previousDependentSectionsApplicationKeyRef.current === applicationLoadKey;

    previousDependentSectionsApplicationKeyRef.current = applicationLoadKey;

    if (!isSameApplication) {
      setDependentSectionDirtyMap(dependentSectionDirtyInitialState);
      setDependentSectionSavedMap({
        principal: isPrincipalSectionDataComplete,
        beneficiary: isBeneficiarySectionDataComplete,
        guaranteeForm: isGuaranteeFormSectionDataComplete,
      });

      return;
    }

    setDependentSectionSavedMap((previousState) => ({
      principal: previousState.principal || isPrincipalSectionDataComplete,
      beneficiary: previousState.beneficiary || isBeneficiarySectionDataComplete,
      guaranteeForm: previousState.guaranteeForm || isGuaranteeFormSectionDataComplete,
    }));
  }, [
    applicationLoadKey,
    isBeneficiarySectionDataComplete,
    isGuaranteeFormSectionDataComplete,
    isPrincipalSectionDataComplete,
  ]);
  React.useEffect(() => {
    if (previousSelectionApplicationKeyRef.current === applicationLoadKey) {
      return;
    }

    previousSelectionApplicationKeyRef.current = applicationLoadKey;
    hasSavedMainDealInCurrentSessionRef.current = false;
    setSavedMainDealId(applicationSavedMainDealId);
    setHasTenderPatchSuccess(false);
    setHasMainDealPatchSuccess(false);
    setHasTenderSelectionChanges(false);
    setHasMainDealSelectionChanges(false);
    setRequiresNewTenderSelection(false);
    setRequiresNewMainDealSelection(false);
  }, [applicationLoadKey, applicationSavedMainDealId]);

  React.useEffect(() => {
    if (hasSavedMainDealInCurrentSessionRef.current) {
      return;
    }

    setSavedMainDealId(applicationSavedMainDealId);
  }, [applicationSavedMainDealId]);

  React.useEffect(() => {
    if (!isGuaranteeTypeChanged) {
      return;
    }

    setHasTenderPatchSuccess(false);
    setHasMainDealPatchSuccess(false);
    setRequiresNewTenderSelection(true);
    setRequiresNewMainDealSelection(true);
  }, [isGuaranteeTypeChanged]);
  const handleDependentSectionDirtyChange = React.useCallback(
    (section: TGuaranteesRootDependentSectionKey, isDirty: boolean) => {
      setDependentSectionDirtyMap((previousState) => {
        if (previousState[section] === isDirty) {
          return previousState;
        }

        return {
          ...previousState,
          [section]: isDirty,
        };
      });
    },
    []
  );
  const hasDependentSectionChanges =
    shouldShowDependentSections && Object.values(dependentSectionDirtyMap).some(Boolean);
  const shouldRequireActionChanges = requireChangesToEnableActions && isNextStepAvailable;
  const canSaveContract =
    !!selectedContract?.value && isSavedGuaranteeTypeSelected && applicationLoadKey !== 'new';
  const { accountSectionProps } = useRootAccountSection({
    accounts,
    applicationLoadKey,
    guaranteeCurrencyOptions,
    isLoadingAccounts,
    isLoadingCurrencies,
    isLoadingPrincipalDebitAccounts,
    isLoadingSecurityCurrencies,
    isSavedGuaranteeTypeSelected,
    isSavingAccount,
    lockedUntilSavedHint,
    principalDebitAccounts,
    onAccountsLoad,
    onPrincipalDebitAccountsLoad,
    onSecurityCurrenciesLoad,
    onSecretSubmit,
  });
  const rootApplicationSectionContextValue = React.useMemo(
    () => ({
      isReadOnly,
      requireChangesToEnableActions: shouldRequireActionChanges,
      onDependentSectionDirtyChange: handleDependentSectionDirtyChange,
    }),
    [handleDependentSectionDirtyChange, isReadOnly, shouldRequireActionChanges]
  );
  React.useEffect(() => {
    if (
      !shouldLoadGuaranteesDependentSections({
        applicationLoadKey,
        loadedDependentSectionsKey: loadedDependentSectionsRef.current,
        shouldShowDependentSections,
      })
    ) {
      return;
    }

    loadedDependentSectionsRef.current = applicationLoadKey;
    void Promise.all([
      onPrincipalLoad(),
      onBeneficiaryLoad(),
      onBaseRelationsLoad(),
      onGuaranteeFormLoad(),
    ]);
  }, [
    applicationLoadKey,
    onBaseRelationsLoad,
    onBeneficiaryLoad,
    onGuaranteeFormLoad,
    onPrincipalLoad,
    shouldShowDependentSections,
  ]);

  const handleContractChange = (option: TGuaranteesContractOption) => {
    if (selectedContract && areSameContractOptions(option, selectedContract)) {
      clearGuaranteesContractSelection(setValue);
      setHasMainDealPatchSuccess(false);
      setHasMainDealSelectionChanges(true);

      return;
    }

    applyGuaranteesContractOptionToForm(option, setValue);
    setHasMainDealPatchSuccess(false);
    setHasMainDealSelectionChanges(option.mainDealId.trim() !== savedMainDealId);
  };

  const handleContractSave = async () => {
    const contract = selectedContract;

    if (!contract) {
      return;
    }

    const isMainDealSelected = await onMainDealSelect(contract);

    if (!isMainDealSelected) {
      return;
    }

    setHasMainDealPatchSuccess(true);
    setHasMainDealSelectionChanges(false);
    setRequiresNewMainDealSelection(false);
    hasSavedMainDealInCurrentSessionRef.current = true;
    setSavedMainDealId(contract.mainDealId.trim());
    saveGuaranteesStoredContractSelection(applicationLoadKey, contract);
    applyGuaranteesContractOptionToForm(contract, setValue);

    MAIN_DEAL_FIELD_NAMES.forEach((fieldName) => {
      resetField(fieldName, {
        defaultValue: getValues(fieldName),
      });
    });
  };
  const handleTenderSearch = React.useCallback(
    async (tenderPublicId: string) => {
      if (tenderPublicId.trim() !== savedTenderPublicId) {
        setHasTenderPatchSuccess(false);
        setHasTenderSelectionChanges(true);
      }

      await onTenderSearch(tenderPublicId);
    },
    [onTenderSearch, savedTenderPublicId]
  );
  const handleTenderSelectionChange = React.useCallback((hasChanges: boolean) => {
    setHasTenderSelectionChanges(hasChanges);

    if (hasChanges) {
      setHasTenderPatchSuccess(false);
    }
  }, []);
  const handleTenderSelect = React.useCallback(
    async (selectedLotIds: string[]) => {
      const isTenderSelected = await onTenderSelect(selectedLotIds);

      if (isTenderSelected) {
        setHasTenderPatchSuccess(true);
        setHasTenderSelectionChanges(false);
        setRequiresNewTenderSelection(false);
      }

      return isTenderSelected;
    },
    [onTenderSelect]
  );
  const handleRootSectionSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    const isSaved = await onSubmit(event);

    if (!isSaved) {
      return;
    }

    ROOT_SECTION_FIELD_NAMES.forEach((fieldName) => {
      resetField(fieldName, {
        defaultValue: getValues(fieldName),
      });
    });
  };

  const handlePrincipalSubmit = React.useCallback(
    async (...args: Parameters<typeof onPrincipalSubmit>) => {
      const isSaved = await onPrincipalSubmit(...args);

      if (isSaved) {
        setDependentSectionSavedMap((previousState) => ({
          ...previousState,
          principal: true,
        }));
        handleDependentSectionDirtyChange('principal', false);
      }

      return isSaved;
    },
    [handleDependentSectionDirtyChange, onPrincipalSubmit]
  );
  const handleBeneficiarySubmit = React.useCallback(
    async (...args: Parameters<typeof onBeneficiarySubmit>) => {
      const isSaved = await onBeneficiarySubmit(...args);

      if (isSaved) {
        setDependentSectionSavedMap((previousState) => ({
          ...previousState,
          beneficiary: true,
        }));
        handleDependentSectionDirtyChange('beneficiary', false);
      }

      return isSaved;
    },
    [handleDependentSectionDirtyChange, onBeneficiarySubmit]
  );
  const handleBaseRelationsSubmit = React.useCallback(
    async (...args: Parameters<typeof onBaseRelationsSubmit>) => {
      const isSaved = await onBaseRelationsSubmit(...args);

      if (isSaved) {
        handleDependentSectionDirtyChange('baseRelations', false);
      }

      return isSaved;
    },
    [handleDependentSectionDirtyChange, onBaseRelationsSubmit]
  );
  const handleGuaranteeFormSubmit = React.useCallback(
    async (...args: Parameters<typeof onGuaranteeFormSubmit>) => {
      const isSaved = await onGuaranteeFormSubmit(...args);

      if (isSaved) {
        setDependentSectionSavedMap((previousState) => ({
          ...previousState,
          guaranteeForm: true,
        }));
        handleDependentSectionDirtyChange('guaranteeForm', false);
      }

      return isSaved;
    },
    [handleDependentSectionDirtyChange, onGuaranteeFormSubmit]
  );

  const currentValues = {
    ...getValues(),
    ...watchedRootValues,
  } as TGuaranteesRootSectionFormValues;
  const isRootSectionComplete =
    Object.keys(validateGuaranteesRootSectionForm(currentValues)).length === 0;
  const isSecretSectionComplete =
    Object.keys(validateGuaranteesSecretSectionForm(currentValues)).length === 0;
  const areDependentSectionsComplete =
    shouldShowDependentSections &&
    (isPrincipalSectionDataComplete || dependentSectionSavedMap.principal) &&
    (isBeneficiarySectionDataComplete || dependentSectionSavedMap.beneficiary) &&
    (isGuaranteeFormSectionDataComplete || dependentSectionSavedMap.guaranteeForm);
  const isStep1Complete =
    isRootSectionComplete &&
    isSecretSectionComplete &&
    areDependentSectionsComplete &&
    areGuaranteesRequiredSelectionsComplete({
      hasMainDealSelection,
      hasTenderSelection,
      isProzorroTender: isSavedProzorroTender,
    });
  const isStep1Busy =
    isSaving ||
    isSavingAccount ||
    isSavingPrincipal ||
    isSavingBeneficiary ||
    isSavingBaseRelations ||
    isSavingGuaranteeForm ||
    isCheckingApplication ||
    isLoadingPrincipal ||
    isLoadingBeneficiary ||
    isLoadingBaseRelations ||
    isLoadingGuaranteeForm;
  const isCheckApplicationDisabled = isGuaranteesCheckApplicationDisabled({
    canCheckApplication,
    hasDependentSectionChanges,
    hasGuaranteeTypeChanges,
    isReadOnly,
    isStep1Busy,
    isStep1Complete,
  });
  const checkApplicationStartIcon = isCheckingApplication ? (
    <CircularProgress size={18} />
  ) : undefined;

  return (
    <GuaranteesRootApplicationSectionProvider value={rootApplicationSectionContextValue}>
      <Box sx={guaranteesClientPageStyles.applicationFlowStack}>
        <Box
          component="form"
          onSubmit={(event) => void handleRootSectionSubmit(event)}
          sx={guaranteesClientPageStyles.applicationFlowStack}
        >
          <GuaranteeTypeSection
            isGuaranteeTypeSelected={isGuaranteeTypeSelected}
            isOtherOperationType={isOtherOperationType}
            isProzorroTender={isProzorroTender}
            guaranteeCurrencyOptions={guaranteeCurrencyOptions}
            isLoadingCurrencies={isLoadingCurrencies}
            isSaving={isSaving}
            hasChanges={hasGuaranteeTypeChanges}
          />

          <ProzorroTenderSection
            displayedTenderPublicId={displayedTenderPublicId}
            savedTenderPublicId={savedTenderPublicId}
            isSavedProzorroTender={isSavedProzorroTender}
            isSearchingTender={isSearchingTender}
            isSelectingTender={isSelectingTender}
            prozorroDisabledHint={prozorroDisabledHint}
            prozorroTender={prozorroTender}
            applicationTenderInfo={applicationTenderInfo}
            onTenderSearch={handleTenderSearch}
            onTenderSelect={handleTenderSelect}
            onTenderSelectionChange={handleTenderSelectionChange}
            isTenderSelectionRequired={requiresNewTenderSelection}
          />

          <MainDealSection
            canSaveContract={canSaveContract}
            contractOptions={baseContractOptions}
            isLoadingMainDeals={isLoadingMainDeals}
            isSavedGuaranteeTypeSelected={isSavedGuaranteeTypeSelected}
            isSelectingMainDeal={isSelectingMainDeal}
            hasChanges={hasMainDealSelectionChanges || requiresNewMainDealSelection}
            lockedUntilSavedHint={lockedUntilSavedHint}
            selectedContract={selectedContract}
            onContractSelect={handleContractChange}
            onSave={handleContractSave}
          />

          <AccountSection {...accountSectionProps} />
        </Box>

        {shouldShowDependentSections ? (
          <>
            <PrincipalSection
              principal={principal}
              isLoading={isLoadingPrincipal}
              isSaving={isSavingPrincipal}
              onSubmit={handlePrincipalSubmit}
            />
            <BeneficiarySection
              beneficiary={beneficiary}
              isLoading={isLoadingBeneficiary}
              isSaving={isSavingBeneficiary}
              onSubmit={handleBeneficiarySubmit}
            />
            <BaseRelationsSection
              baseRelations={baseRelations}
              isLoading={isLoadingBaseRelations}
              isSaving={isSavingBaseRelations}
              isProzorroTender={isSavedProzorroTender}
              onSubmit={handleBaseRelationsSubmit}
            />
            <GuaranteeFormSection
              guaranteeForm={guaranteeForm}
              isLoading={isLoadingGuaranteeForm}
              isSaving={isSavingGuaranteeForm}
              onSubmit={handleGuaranteeFormSubmit}
            />
          </>
        ) : (
          <Typography sx={guaranteesClientPageStyles.flowLockHint}>
            {guaranteesClientText.principalSelectAccountHint}
          </Typography>
        )}

        <Box sx={guaranteesClientPageStyles.flowPageActionsCentered}>
          {!isNextStepAvailable && (
            <Typography sx={guaranteesClientPageStyles.flowLockHint}>
              {guaranteesClientText.saveGuaranteeTypeBeforeContinue}
            </Typography>
          )}

          <Button
            type="button"
            variant="contained"
            disabled={isCheckApplicationDisabled}
            onClick={() => void onCheckApplication()}
            startIcon={checkApplicationStartIcon}
            sx={dataFormStyles.documentPrimaryAction}
          >
            {checkApplicationButtonLabel}
          </Button>
        </Box>
      </Box>
    </GuaranteesRootApplicationSectionProvider>
  );
};

export default GuaranteesRootApplicationSection;
